/**
 * React Query hooks for Briefs.
 *
 * Resolve the active ``HttpClient`` via ``useHttpClient()`` so the same
 * hooks work in admin and portal trees.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useHttpClient } from "@/core/http/HttpClientContext";
import { briefsService } from "../services/briefsService";
import {
  Brief,
  BriefListItem,
  BriefNote,
  CreateBriefRequest,
  UpdateBriefRequest,
  AddNoteRequest,
  briefKeys,
} from "../types";

export function useBriefsQuery(clientId: string | undefined) {
  const http = useHttpClient();
  return useQuery({
    queryKey: clientId ? briefKeys.list(clientId) : ["briefs", "none"],
    queryFn: async () => {
      if (!clientId) return [];
      return briefsService.getBriefs(http, clientId);
    },
    enabled: !!clientId,
  });
}

export function usePortalBriefsQuery(clientId: string) {
  const http = useHttpClient();
  return useQuery({
    // Keyed by client: the endpoint scopes by JWT, and the creator can
    // switch workspaces without remounting this tree — an unscoped key
    // would keep serving the previous client's cached list.
    queryKey: briefKeys.portalList(clientId),
    queryFn: () => briefsService.getPortalBriefs(http),
  });
}

export function useBriefQuery(briefId: string | undefined) {
  const http = useHttpClient();
  const isValidId = briefId && briefId !== "null" && briefId !== "undefined";
  return useQuery({
    queryKey: isValidId ? briefKeys.detail(briefId) : ["briefs", "none"],
    queryFn: async () => {
      if (!isValidId) return null;
      return briefsService.getBrief(http, briefId);
    },
    enabled: !!isValidId,
  });
}

export function useBriefNotesQuery(briefId: string | undefined) {
  const http = useHttpClient();
  const isValidId = briefId && briefId !== "null" && briefId !== "undefined";
  return useQuery({
    queryKey: isValidId ? briefKeys.notes(briefId) : ["briefs", "none"],
    queryFn: async () => {
      if (!isValidId) return [];
      return briefsService.getBriefNotes(http, briefId);
    },
    enabled: !!isValidId,
  });
}

export function useCreateBriefMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateBriefRequest) =>
      briefsService.createBrief(http, clientId, data),
    onSuccess: () => {
      // Refetch so the new card has the full BriefListItem shape
      // (calendarEventCount, calendarEvents, drive URLs from server) and
      // the click handler renders cleanly without missing fields.
      queryClient.invalidateQueries({ queryKey: briefKeys.list(clientId) });
    },
  });
}

export function useUpdateBriefMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      briefId,
      data,
    }: {
      briefId: string;
      data: UpdateBriefRequest;
    }) => briefsService.updateBrief(http, briefId, data),
    onMutate: async ({ briefId, data }) => {
      await queryClient.cancelQueries({ queryKey: briefKeys.list(clientId) });
      const previousBriefs = queryClient.getQueryData<BriefListItem[]>(
        briefKeys.list(clientId)
      );
      if (previousBriefs) {
        queryClient.setQueryData<BriefListItem[]>(briefKeys.list(clientId), (old) =>
          old?.map((brief) =>
            brief.id === briefId ? { ...brief, ...data } : brief
          )
        );
      }
      return { previousBriefs };
    },
    onError: (_err, _variables, context) => {
      if (context?.previousBriefs) {
        queryClient.setQueryData(briefKeys.list(clientId), context.previousBriefs);
      }
    },
    onSettled: (_data, _error, { briefId }) => {
      queryClient.invalidateQueries({ queryKey: briefKeys.detail(briefId) });
    },
  });
}

export function useDeleteBriefMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (briefId: string) => {
      await briefsService.deleteBrief(http, briefId);
      return briefId;
    },
    onMutate: async (briefId) => {
      await queryClient.cancelQueries({ queryKey: briefKeys.list(clientId) });
      const previousBriefs = queryClient.getQueryData<BriefListItem[]>(
        briefKeys.list(clientId)
      );
      if (previousBriefs) {
        queryClient.setQueryData<BriefListItem[]>(briefKeys.list(clientId), (old) =>
          old?.filter((brief) => brief.id !== briefId)
        );
      }
      return { previousBriefs };
    },
    onError: (_err, _briefId, context) => {
      if (context?.previousBriefs) {
        queryClient.setQueryData(briefKeys.list(clientId), context.previousBriefs);
      }
    },
  });
}

export function useShareBriefMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (briefId: string) => briefsService.shareBrief(http, briefId),
    onSuccess: (_shareResponse, briefId) => {
      queryClient.invalidateQueries({ queryKey: briefKeys.detail(briefId) });
      queryClient.invalidateQueries({ queryKey: briefKeys.list(clientId) });
    },
  });
}

export function useAddIdeaToBriefMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      briefId,
      ideaId,
    }: {
      briefId: string;
      ideaId: string;
    }) => briefsService.addIdeaToBrief(http, briefId, ideaId),
    onSuccess: (updatedBrief) => {
      queryClient.setQueryData(briefKeys.detail(updatedBrief.id), updatedBrief);
      queryClient.invalidateQueries({ queryKey: briefKeys.list(clientId) });
    },
  });
}

export function useRemoveIdeaFromBriefMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      briefId,
      ideaId,
    }: {
      briefId: string;
      ideaId: string;
    }) => briefsService.removeIdeaFromBrief(http, briefId, ideaId),
    onSuccess: (updatedBrief) => {
      queryClient.setQueryData(briefKeys.detail(updatedBrief.id), updatedBrief);
      queryClient.invalidateQueries({ queryKey: briefKeys.list(clientId) });
    },
  });
}

export function useAddBriefNoteMutation(briefId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AddNoteRequest) =>
      briefsService.addBriefNote(http, briefId, data),
    onSuccess: (newNote) => {
      queryClient.setQueryData<BriefNote[]>(briefKeys.notes(briefId), (old) =>
        old ? [...old, newNote] : [newNote]
      );
      queryClient.invalidateQueries({ queryKey: briefKeys.detail(briefId) });
    },
  });
}

export function useUpdateBriefNoteMutation(briefId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      noteId,
      data,
    }: {
      noteId: string;
      data: AddNoteRequest;
    }) => briefsService.updateBriefNote(http, briefId, noteId, data),
    onSuccess: (updatedNote) => {
      queryClient.setQueryData<BriefNote[]>(briefKeys.notes(briefId), (old) =>
        old?.map((note) => (note.id === updatedNote.id ? updatedNote : note))
      );
      queryClient.invalidateQueries({ queryKey: briefKeys.detail(briefId) });
    },
  });
}

export function useDeleteBriefNoteMutation(briefId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string) => {
      await briefsService.deleteBriefNote(http, briefId, noteId);
      return noteId;
    },
    onMutate: async (noteId) => {
      await queryClient.cancelQueries({ queryKey: briefKeys.notes(briefId) });
      const previousNotes = queryClient.getQueryData<BriefNote[]>(briefKeys.notes(briefId));
      if (previousNotes) {
        queryClient.setQueryData<BriefNote[]>(briefKeys.notes(briefId), (old) =>
          old?.filter((note) => note.id !== noteId)
        );
      }
      return { previousNotes };
    },
    onError: (_err, _noteId, context) => {
      if (context?.previousNotes) {
        queryClient.setQueryData(briefKeys.notes(briefId), context.previousNotes);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: briefKeys.detail(briefId) });
    },
  });
}

export function usePinCalendarEventMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      briefId,
      calendarEventId,
    }: {
      briefId: string;
      calendarEventId: string;
    }) => briefsService.pinCalendarEvent(http, briefId, calendarEventId),
    onSuccess: (_data, { briefId }) => {
      queryClient.invalidateQueries({ queryKey: briefKeys.detail(briefId) });
      queryClient.invalidateQueries({ queryKey: briefKeys.list(clientId) });
    },
  });
}

export function useUnpinCalendarEventMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      briefId,
      calendarEventId,
    }: {
      briefId: string;
      calendarEventId: string;
    }) => briefsService.unpinCalendarEvent(http, briefId, calendarEventId),
    onSuccess: (_data, { briefId }) => {
      queryClient.invalidateQueries({ queryKey: briefKeys.detail(briefId) });
      queryClient.invalidateQueries({ queryKey: briefKeys.list(clientId) });
    },
  });
}

export function useGenerateBriefContextMutation(clientId: string) {
  const http = useHttpClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (briefId: string) => briefsService.generateContext(http, briefId),
    onSuccess: (result, briefId) => {
      queryClient.setQueryData<Brief>(briefKeys.detail(briefId), (old) =>
        old ? { ...old, distilledContext: result.distilledContext } : old
      );
      queryClient.invalidateQueries({ queryKey: briefKeys.list(clientId) });
    },
  });
}

export const briefCacheUtils = {
  invalidateAll(
    clientId: string,
    queryClient: ReturnType<typeof useQueryClient>
  ): void {
    queryClient.invalidateQueries({ queryKey: briefKeys.list(clientId) });
  },
};
